import React, { useEffect, useState } from 'react';
import cls from './HomePage.module.scss';
import { ReactComponent as UserGroupIcon } from 'src/assets/img/icons/users-group-icon.svg';
import { ReactComponent as UserIcon } from 'src/assets/img/icons/user-1-icon.svg';
import { ReactComponent as LockIcon } from 'src/assets/img/icons/lock-icon.svg';
import { ReactComponent as UserOnlineIcon } from 'src/assets/img/icons/user-online-icon.svg';
import { ReactComponent as DesktopIcon } from 'src/assets/img/icons/desktop.svg';
import TariffsPopup from './components/TariffsPopup/TariffsPopup';
import { useGetUsersQuery } from 'src/services/users/goUsers';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';
import useSearchQueryParams from 'src/hooks/useSearchQueryParams';
import Pagination from 'src/UI/Pagination/Pagination';
import SubscriptionExpiredModal from 'src/pages/SubscriptionsPage/components/SubscriptionExpiredModal/SubscriptionExpiredModal';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import selectors from 'src/redux/selectors';
import DashboardStats from './components/DashboardStats/DashboardStats';
import GaugeChart from '../../UI/charts/GaugeChart/GaugeChart';
import DoughnutChart from '../../UI/charts/DoughnutChart/DoughnutChart';
import ChartBlock from '../StatisticsPage/components/ChartBlock';
import { useGetDashboardQuery } from '../../services/dashboard/dashboard';
import DoughnutChartSkeleton from '../../UI/skeletons/DoughnutChartSkeleton';
import GaugeChartSkeleton from '../../UI/skeletons/GaugeChartSkeleton';

const HomePage = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'homepage' });
  const { t: tg } = useTranslation('translation');
  const { t: tSubscriptionsExpired } = useTranslation('translation', {
    keyPrefix: 'subscription-expired',
  });
  const { params, setParams } = useSearchQueryParams({});
  const { isLoading, data } = useGetUsersQuery(params);
  const { data: dashboardData, isLoading: isDashboardLoading } =
    useGetDashboardQuery({});
  const subscriptionInfo = useTypedSelector(selectors.getSubscriptionInfo);
  const showPP = subscriptionInfo?.subscriptionExpires;
  const [showTarrifsPopup, setShowTarrifsPopup] = useState<boolean>(false);
  const [isShowSubsExpPopup, setShowSubsExpPopup] = useState<boolean>(showPP);
  const onShowSubsExpPopupClose = () => {
    setShowSubsExpPopup(false);
  };

  useEffect(() => {
    if (document) {
      document.body.classList.add(cls['home-page']);

      return () => {
        document.body.classList.remove(cls['home-page']);
      };
    }
  }, []);

  const onlineUsersAmount = data?.data.filter((user) => user.is_online).length;
  const offlineUsersAmount = data?.data.filter(
    (user) => !user.is_online
  ).length;

  const noData = Object.values(params).length
    ? tg('table-no-data-filter')
    : tg('table-no-data-new');

  return (
    <div className="pt-10">
      <DashboardStats
        leads={dashboardData?.finances?.orders?.orders_total}
        avg_check={
          dashboardData?.finances?.indicators?.indicators_average_check
        }
        avg_check_percent={
          dashboardData?.finances?.indicators
            ?.indicators_average_check_in_percent
        }
        avg_margin={dashboardData?.finances?.marginality?.marginality_average}
        avg_upsale={
          dashboardData?.finances?.indicators?.indicators_average_upsale
        }
        isLoading={isDashboardLoading}
      />
      <div className={cls['charts-wrapper']}>
        <div className={cls['statistic-chart-wrapper']}>
          <ChartBlock
            statistic={dashboardData}
            statisticLoading={isDashboardLoading}
            isDayChart={true}
            shouldShowAllLine={false}
            chartClassName={cls['chart']}
            isSkeletonLoader={true}
          />
        </div>
        <div className={cls['charts-block-wrapper']}>
          <div className={cls['gauge-charts-wrapper']}>
            {isDashboardLoading ? (
              <GaugeChartSkeleton width={140} height={70} />
            ) : (
              <GaugeChart
                title="Up-Sale"
                values={{
                  percentage:
                    dashboardData?.finances?.additions_with_approve
                      ?.upsale_in_percent || 0,
                  total:
                    dashboardData?.finances?.additions_with_approve?.upsale ||
                    0,
                }}
              />
            )}
            {isDashboardLoading ? (
              <GaugeChartSkeleton width={140} height={70} />
            ) : (
              <GaugeChart
                title="Cross-Sale"
                values={{
                  percentage:
                    dashboardData?.finances?.additions_with_approve
                      ?.crossale_in_percent || 0,
                  total:
                    dashboardData?.finances?.additions_with_approve?.crossale ||
                    0,
                }}
              />
            )}
          </div>
          <div className={cls['doughnut-chart-wrapper']}>
            <p className={cls['doughnut-title']}>Approve</p>
            {isDashboardLoading ? (
              <DoughnutChartSkeleton width={300} height={300} />
            ) : (
              <DoughnutChart
                totalPercent={
                  dashboardData?.finances?.approve?.approve_in_percent || 0
                }
                statusesData={dashboardData?.statuses || {}}
              />
            )}
          </div>
        </div>
      </div>
      <div className={cls['wrapper']}>
        <div className="header-line">
          <div />
          <div className={cls['users-wrapper']}>
            <p className={cls['users-title']}>
              {t('users')} ( <UserGroupIcon className="icon" />
              <span className={`${cls['description-text']} mr-5`}>
                {data?.data?.length}
              </span>
              ){' '}
            </p>

            <div className="align-center gap-5">
              <UserIcon className="icon--18" />
              <span className={cls['description-text']}>
                {onlineUsersAmount}
              </span>
            </div>
            <div className="align-center gap-5">
              <LockIcon className="icon--18" />
              <span className={cls['description-text']}>
                {offlineUsersAmount}
              </span>
            </div>
          </div>
        </div>
        {isLoading ? (
          <></>
        ) : data?.data?.length ? (
          [...data.data]
            .sort((a, b) => {
              if (a.is_online === b.is_online) return 0;
              return a.is_online ? -1 : 1;
            })
            .map((item, index) => {
              const isUserOnline = item?.is_online;

              return (
                <div key={index} className={cls['data-wrapper']}>
                  <div className={cls['data-item']}>
                    <div className={cls['column-1']}>
                      <div className="align-center">
                        {isUserOnline ? (
                          <UserOnlineIcon className="icon" />
                        ) : (
                          <UserIcon className="icon" />
                        )}
                        <span
                          className={`${cls['description-text']} ${
                            isUserOnline ? cls['text-colored'] : ''
                          }`}
                        >
                          {item.full_name}
                          <span className="text-gray ml-5">({item?.role})</span>
                        </span>
                      </div>
                    </div>
                    <div className={cls['column-2']}>
                      {item?.ip_address ? (
                        <>
                          {isUserOnline ? (
                            <div className="align-center width-100">
                              <DesktopIcon className="icon" />
                              <span className={cls['description-text']}>
                                {item?.ip_address}
                              </span>
                            </div>
                          ) : (
                            <div className="align-center width-100">
                              <span className={cls['description-text']}>
                                {t('offline')}
                              </span>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="align-center width-100"> </div>
                      )}

                      {item?.country && (
                        <ReactCountryFlag
                          countryCode={item?.country?.iso2}
                          svg
                          style={{ margin: '0 auto' }}
                        />
                      )}
                    </div>
                    <div
                      className={`${cls['column-3']} ${cls['description-text']}`}
                    >
                      {item?.last_online}
                    </div>
                  </div>
                </div>
              );
            })
        ) : (
          <b
            style={{
              display: 'inline-block',
              textAlign: 'center',
              marginTop: '20px',
            }}
            className={'width-full'}
          >
            {noData}
          </b>
        )}
      </div>
      <div className={cls['wrapper-white-space']}></div>
      <TariffsPopup
        show={showTarrifsPopup}
        close={() => {
          setShowTarrifsPopup(false);
        }}
      />
      <Pagination params={params} setParams={setParams} meta={data?.meta} />
      <SubscriptionExpiredModal
        t={tSubscriptionsExpired}
        subscription={subscriptionInfo?.subscription}
        open={isShowSubsExpPopup}
        close={() => {
          onShowSubsExpPopupClose();
        }}
        title={tSubscriptionsExpired('modal-title-now')}
        isCanCloseModal={true}
        hideCloseIcon={false}
        expired={false}
      />
    </div>
  );
};
export default HomePage;
